import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import logo from '../../assets/logo.png';
import illustration from '../../assets/gps-concept-light.svg';
import api from '../../services/api';
import './styles.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignIn() {
  const navigate = useNavigate();

  const emailRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/home');
      return;
    }
    emailRef.current?.focus();
  }, [navigate]);

  const validate = () => {
    let ok = true;
    if (!email) {
      setEmailError('Informe seu e-mail');
      ok = false;
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailError('E-mail inválido');
      ok = false;
    } else {
      setEmailError('');
    }

    if (!password) {
      setPasswordError('Informe sua senha');
      ok = false;
    } else {
      setPasswordError('');
    }
    return ok;
  };

  const showError = (message) => setSnackbar({ open: true, message, severity: 'error' });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });

      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(data.user));

      navigate('/home');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        showError('E-mail ou senha inválidos.');
      } else if (status === 400) {
        showError('Preencha os campos corretamente.');
      } else if (!err?.response) {
        showError('Sem conexão com o servidor. Verifique sua rede.');
      } else {
        showError('Falha ao entrar. Tente novamente em instantes.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <Paper elevation={3} className="login-card">
        <section className="login-form-side">
          <Box className="login-brand">
            <img src={logo} alt="OpenMaps" className="login-logo" />
          </Box>

          <Typography variant="h5" component="h1" className="login-title">
            Entrar no painel
          </Typography>
          <Typography variant="body2" color="text.secondary" className="login-subtitle">
            Acesse com sua conta OpenMaps.
          </Typography>

          <form noValidate autoComplete="off" onSubmit={handleLogin}>
            <Stack spacing={2}>
              <TextField
                inputRef={emailRef}
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                error={!!emailError}
                helperText={emailError}
                disabled={submitting}
                fullWidth
                autoComplete="username"
              />

              <TextField
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                error={!!passwordError}
                helperText={passwordError}
                disabled={submitting}
                fullWidth
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                size="large"
                variant="contained"
                disabled={submitting}
                sx={{ height: 52, mt: 1 }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
              </Button>
            </Stack>
          </form>
        </section>

        <aside className="login-illustration-side" aria-hidden>
          <img src={illustration} alt="" />
        </aside>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
